import React, {useEffect, useState} from 'react';
import Avatar from 'react-avatar';
import {ChevronDownIcon, SparklesIcon, XMarkIcon} from '@heroicons/react-v2/24/outline';

import Link from '../Link';
import KarmaPill from '../KarmaPill';
import Search from '../Search';
import Brand from '../Brand';
import SidebarNav, {resolveLink} from './SidebarNav';
import {SidebarContext, useSidebar} from './SidebarContext';
import getActionLink from './actionLink';
import SidebarMenu from '../../constants/sidebarMenu';
import {useAppContext, useAnalytics} from '../../hooks';
import {cx} from '../../util';

/**
 * The sidebar as an off-canvas drawer, for viewports narrower than `lg` where
 * there is no room to keep it on screen.
 *
 * Nested items expand inline here rather than flying out horizontally, and the
 * sidebar context is overridden so labels are always visible regardless of the
 * user's desktop collapse preference.
 *
 * @param {string} props.upgradeLink Destination for the Upgrade CTA.
 */
const MobileNavDrawer = ({upgradeLink = '/upgrade?src=nav'}) => {
  const appContext = useAppContext();
  const {tracker} = useAnalytics();
  const {currentUser} = appContext;
  const sidebar = useSidebar();
  const {mobileOpen, setMobileOpen} = sidebar;
  // The account links live behind the profile row: expanded they eat half
  // the drawer, and they're the least-used items in it.
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen, setMobileOpen]);

  const actionLink = getActionLink(appContext, upgradeLink);
  const accountItems = SidebarMenu.accountItems.filter((item) => !item.shouldHide?.(appContext));

  const close = () => {
    setMobileOpen(false);
    setAccountOpen(false);
  };

  const trackAndClose = (item) => {
    tracker.headerClickMenu({platform: 'mobile-sidebar', title: item.title, link: resolveLink(item.link, appContext)});
    close();
  };

  return (
    <div className={cx('lg:hidden', !mobileOpen && 'pointer-events-none')} aria-hidden={!mobileOpen}>
      {/* Scrim */}
      <button
        type="button"
        tabIndex={mobileOpen ? 0 : -1}
        aria-label="Close navigation menu"
        onClick={close}
        className={cx(
          'fixed inset-0 z-40 bg-gray-900/40 transition-opacity duration-200',
          mobileOpen ? 'opacity-100' : 'opacity-0',
        )}
      />

      <div
        role="dialog"
        aria-modal={mobileOpen ? 'true' : undefined}
        aria-label="Navigation menu"
        className={cx(
          'fixed inset-y-0 left-0 z-50 flex w-full flex-col bg-white transition-[translate,box-shadow] duration-200 ease-out xs:w-[290px] xs:max-w-[85vw]',
          mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 shrink-0 items-center justify-between px-4">
          <Brand onClick={close} markClassName="h-6 w-6" wordmarkClassName="ml-2.5 mt-[3px] h-4" />
          <button
            type="button"
            onClick={close}
            aria-label="Close navigation menu"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
          <div className="px-1 pb-4">
            <Search className="w-full" expanded onClick={close} />
          </div>

          <SidebarContext.Provider
            value={{
              ...sidebar, collapsed: false, showLabels: true, hoverExpanded: false,
            }}
          >
            <SidebarNav inline onNavigate={close} />
          </SidebarContext.Provider>

          {actionLink && (
            <Link
              href={actionLink.link}
              onClick={() => {
                if (actionLink.isUpgrade) tracker.headerClickUpgrade({source: 'mobile-sidebar'});
                close();
              }}
              className={cx(
                'mt-5 flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-semibold',
                actionLink.isUpgrade
                  ? 'bg-indigo text-white'
                  : 'border border-indigo-200 text-indigo',
              )}
            >
              {actionLink.isUpgrade && <SparklesIcon className="h-4 w-4 shrink-0" aria-hidden="true" />}
              {actionLink.text}
            </Link>
          )}
        </div>

        {currentUser && (
          <div className="shrink-0 border-t border-gray-200 p-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setAccountOpen((prev) => !prev)}
                aria-expanded={accountOpen}
                className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg p-1 -m-1 text-left transition-colors hover:bg-gray-100/80"
              >
                <Avatar
                  name={currentUser.displayName}
                  src={currentUser.profile_img || ''}
                  color="#EEEBFB"
                  fgColor="#5524e0"
                  size={32}
                  round
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-gray-900">
                    {currentUser.displayName}
                  </div>
                  <div className="truncate text-xs text-gray-500">{currentUser.email}</div>
                </div>
                <ChevronDownIcon
                  className={cx('h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200', accountOpen && 'rotate-180')}
                  aria-hidden="true"
                />
              </button>
              <KarmaPill
                score={currentUser.karma}
                outline
                onClick={() => {
                  tracker.karmaClickScore({source: 'mobile-sidebar'});
                  close();
                }}
              />
            </div>
            {accountOpen && (
              <ul className="mt-2 flex flex-col">
                {accountItems.map((item) => (
                  <li key={item.title}>
                    <Link
                      href={resolveLink(item.link, appContext)}
                      forceAnchor={item.forceAnchor}
                      onClick={() => trackAndClose(item)}
                      className="block rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100/80 hover:text-gray-900"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileNavDrawer;
